/**
 * MapAnything GLB Inference Plugin
 *
 * Video -> semantic mesh (.glb) with Pages behavior via MapAnything.
 * Also creates a Timeline + animated camera frustum visualization.
 */

import type { SceneObject, TraceScene } from 'trace-viewer/scene';
import type { PagesData } from 'trace-viewer/behaviors';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Matrix, Vector3, Quaternion } from '@babylonjs/core/Maths/math.vector';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import { Animation as BabylonAnimation } from '@babylonjs/core/Animations/animation';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import '@babylonjs/core/Meshes/Builders/linesBuilder';
import { declareInferencePlugin } from '../plugins/inference-adapter';
import { createTimelineWidget } from '../timeline-widget';
import {
    INFERENCE_API_URL,
    getVideoBlob,
    createObjectFromInference,
    inferencePost,
    parseFrameMetadata,
    computeAverageFrameDuration,
    type PageFrameMetadata,
    type InferenceStatusCallback,
} from './shared';

// ── Camera Frustum Visualization ───────────────────────────

/**
 * Create a SceneObject with a wireframe camera frustum that animates through
 * camera poses from frame metadata. The frustum is registered as an embedded
 * animation clip so the Animation behavior (and Timeline) can drive it.
 */
async function createCameraFrustum(
    parentObject: SceneObject,
    frames: PageFrameMetadata[],
): Promise<SceneObject | null> {
    const traceScene = parentObject.traceScene;
    const scene = parentObject.babylonScene;

    const framesWithPoses = frames.filter(
        (f): f is PageFrameMetadata & { cameraPose: number[]; timestamp: number } =>
            f.cameraPose != null && f.cameraPose.length === 16 && f.timestamp != null,
    );
    if (framesWithPoses.length < 2) return null;

    // Create a SceneObject for the camera, under its parent from the start —
    // an object never passes through the top of the scene on its way somewhere.
    const cameraObject = await traceScene.createSceneObject('Camera', parentObject);

    // Coordinate system correction: OpenCV camera extrinsics use a different
    // handedness than Babylon.js. Apply a scale(-1, 1, -1) correction node
    // between the animated camera and its visual children.
    const correctionNode = new TransformNode('CameraCorrection', scene);
    correctionNode.parent = cameraObject.transform;
    correctionNode.scaling.set(-1, 1, -1);

    // Build green wireframe frustum from camera intrinsics (if available).
    // Intrinsics matrix (row-major 9 elements): [fx, 0, cx, 0, fy, cy, 0, 0, 1]
    const firstIntrinsics = framesWithPoses.find(f => f.cameraIntrinsics?.length === 9)?.cameraIntrinsics;
    const frustumScale = 25;
    let tl: Vector3, tr: Vector3, br: Vector3, bl: Vector3;

    if (firstIntrinsics) {
        const fx = firstIntrinsics[0];
        const fy = firstIntrinsics[4];
        const cx = firstIntrinsics[2];
        const cy = firstIntrinsics[5];
        const w = cx * 2;
        const h = cy * 2;
        const d = frustumScale;
        tl = new Vector3((-cx / fx) * d, (-cy / fy) * d, d);
        tr = new Vector3(((w - cx) / fx) * d, (-cy / fy) * d, d);
        br = new Vector3(((w - cx) / fx) * d, ((h - cy) / fy) * d, d);
        bl = new Vector3((-cx / fx) * d, ((h - cy) / fy) * d, d);
    } else {
        const s = frustumScale * 0.5;
        const d = frustumScale;
        tl = new Vector3(-s, s, d);
        tr = new Vector3(s, s, d);
        br = new Vector3(s, -s, d);
        bl = new Vector3(-s, -s, d);
    }
    const apex = new Vector3(0, 0, 0);

    const frustumMesh = MeshBuilder.CreateLineSystem('CameraFrustum', {
        lines: [
            [apex, tl], [apex, tr], [apex, br], [apex, bl],
            [tl, tr], [tr, br], [br, bl], [bl, tl],
        ],
    }, scene);
    frustumMesh.color = new Color3(0, 1, 0);
    frustumMesh.isPickable = false;
    frustumMesh.parent = correctionNode;

    // Decompose camera poses into position + rotation keyframes
    const fps = 30;
    const posAnim = new BabylonAnimation(
        'CameraPath_position', 'position', fps,
        BabylonAnimation.ANIMATIONTYPE_VECTOR3,
        BabylonAnimation.ANIMATIONLOOPMODE_CYCLE,
    );
    const rotAnim = new BabylonAnimation(
        'CameraPath_rotationQuaternion', 'rotationQuaternion', fps,
        BabylonAnimation.ANIMATIONTYPE_QUATERNION,
        BabylonAnimation.ANIMATIONLOOPMODE_CYCLE,
    );

    const posKeys: Array<{ frame: number; value: Vector3 }> = [];
    const rotKeys: Array<{ frame: number; value: Quaternion }> = [];

    for (const f of framesWithPoses) {
        const mat = Matrix.FromArray(f.cameraPose);
        const camWorld = Matrix.Invert(mat);
        const scale = new Vector3();
        const rot = new Quaternion();
        const pos = new Vector3();
        camWorld.decompose(scale, rot, pos);

        // Conjugate quaternion to match the (-1,1,-1) correction node
        rot.x = -rot.x;
        rot.y = -rot.y;
        rot.z = -rot.z;

        const frame = f.timestamp * fps;
        posKeys.push({ frame, value: pos });
        rotKeys.push({ frame, value: rot });
    }

    posAnim.setKeys(posKeys);
    rotAnim.setKeys(rotKeys);

    const group = new AnimationGroup('CameraPath', scene);
    group.addTargetedAnimation(posAnim, cameraObject.transform);
    group.addTargetedAnimation(rotAnim, cameraObject.transform);

    cameraObject.setAnimationClips([group]);

    await cameraObject.addBehavior('Animation', {
        clip: 'CameraPath',
        playAutomatically: true,
        wrapMode: 'Loop',
    });

    await cameraObject.addBehavior('Renderer', {});

    return cameraObject;
}

// ── Plugin ─────────────────────────────────────────────────

async function run(sourceObject: SceneObject, traceScene: TraceScene, onStatus?: InferenceStatusCallback): Promise<SceneObject> {
    const videoBlob = await getVideoBlob(sourceObject, traceScene);
    const vidExt = sourceObject.src.split('.').pop()?.toLowerCase() ?? 'mp4';

    const response = await inferencePost('mapanything', () => {
        const formData = new FormData();
        formData.append('video', videoBlob, `video.${vidExt}`);
        formData.append('format', 'glb');
        return [`${INFERENCE_API_URL}/mapanything/predict-video`, { method: 'POST', body: formData }];
    }, undefined, onStatus);
    if (!response.ok) {
        const errText = await response.text();
        throw new Error(`MapAnything API error (${response.status}): ${errText}`);
    }

    const frameMetadata = parseFrameMetadata(response);
    const avgFrameDuration = computeAverageFrameDuration(frameMetadata);

    const glbBlob = await response.blob();
    const assetPath = await traceScene.resources.addAssetWithDedup(
        `assets/${sourceObject.name}_map.glb`,
        glbBlob,
    );

    const pagesData: PagesData = {
        "@type": "Pages",
        pageSource: 'firstChild',
        autoplay: true,
        autoplayLoop: true,
        pageDuration: avgFrameDuration > 0 ? avgFrameDuration : 1,
        ...(frameMetadata.length > 0 ? { frames: frameMetadata } : {}),
    };

    const mapObject = await createObjectFromInference(
        traceScene, sourceObject, assetPath, 'map3d', undefined, [pagesData],
    );

    // Add Timeline to drive both map Pages and camera Animation
    const timestamps = frameMetadata
        .map(f => f.timestamp)
        .filter((t): t is number => t !== undefined)
        .sort((a, b) => a - b);
    const totalDuration = timestamps.length >= 2
        ? timestamps[timestamps.length - 1] - timestamps[0]
        : (frameMetadata.length * (avgFrameDuration > 0 ? avgFrameDuration : 1));

    await mapObject.addBehavior('Timeline', {
        duration: totalDuration > 0 ? totalDuration : 10,
        loop: true,
        playOnStart: true,
    });

    // Spawn an in-scene timeline widget parented to the rig so it travels with it.
    const timelineWidget = await createTimelineWidget(traceScene, mapObject);
    timelineWidget.transform.position.set(0, -0.6, 1.2);

    // Create animated camera frustum as child of the map object
    const cameraObject = await createCameraFrustum(mapObject, frameMetadata);

    // Reparent source video under the camera so it follows the camera pose.
    // Reset local transform to identity so it sits at the camera position.
    if (cameraObject) {
        sourceObject.setParent(cameraObject);
        sourceObject.transform.position.setAll(0);
        sourceObject.transform.rotationQuaternion = null;
        sourceObject.transform.rotation.setAll(0);
        sourceObject.transform.scaling.setAll(1);
    }

    return mapObject;
}

declareInferencePlugin({
    id: 'mapanything-glb',
    sourceType: 'video',
    menuLabel: 'Make Map GLB (MapAnything)',
    menuOrder: 203,

    toolName: 'generate_map_glb',

    run,

    slashCommand: {
        name: 'map-mesh',
        description: 'Convert video to semantic mesh with camera',
        usage: '[object]',
        category: 'inference',
        parseArgs: (args, selected) => ({
            object: args || selected || 'selected',
        }),
    },
});
